import { FormInstance } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { ITradeUnionMember } from 'app/api/tradeUnionMembers/model'
import { IPayloadCreateUnit, UnitType } from 'app/api/unit/model'
import R from 'app/assets/R'
import { CODE_REGEX } from 'app/common/constants'
import { BaseCol } from 'app/components/common/BaseCol'
import { BaseRow } from 'app/components/common/BaseRow'
import { BaseSpace } from 'app/components/common/BaseSpace'
import { BaseForm } from 'app/components/common/forms/BaseForm'
import { BaseInput } from 'app/components/common/inputs/BaseInput'
import { BaseSelect } from 'app/components/common/selects/BaseSelect'
import { useRuleHook } from 'app/hook'
import { useGetSchoolList } from 'app/react-query/hook/school'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export const HookFormCommon = ({
  isLoadingTradeUnionMember,
  tums,
  onChangeUnitType,
  unitType,

  form,
  setTumIdsSelected,
  tumIdsSelected,
}: {
  tums?: ITradeUnionMember[]
  isLoadingTradeUnionMember?: boolean
  unitType?: UnitType
  onChangeUnitType: (type: UnitType) => void
  form?: FormInstance<IPayloadCreateUnit>
  setTumIdsSelected?: (ids: number[]) => void
  tumIdsSelected?: number[]
}) => {
  const { t } = useTranslation()
  const { getNormalRule } = useRuleHook()

  const { data: schoolData, isLoading: loadingSchool } = useGetSchoolList({
    page: 1,
    pageSize: 1000,
  })

  const onChangeTumIds = (ids: number[]) => {
    setTumIdsSelected?.(ids)
    form?.setFieldsValue({
      unionVicePresidentIds: [],
      unionMemberIds: [],
    })
  }

  const tumsCanSelect = useMemo(() => {
    return tums?.filter(item => !!tumIdsSelected?.includes(+(item?.id ?? 0)))
  }, [tums, tumIdsSelected])

  return (
    <BaseSpace size={0}>
      <BaseRow gutter={16} align={'top'}>
        <BaseCol xl={12}>
          <BaseForm.Item
            name={'name'}
            required
            label={'Tên đơn vị'}
            rules={[
              {
                required: true,
                message: t(R.strings.require_field, {
                  field: 'Tên đơn vị',
                }),
              },
            ]}
          >
            <BaseInput placeholder={'Nhập tên'} />
          </BaseForm.Item>
        </BaseCol>

        <BaseCol xl={12}>
          <BaseForm.Item
            name={'code'}
            label={'Mã đơn vị'}
            required
            rules={[
              {
                required: true,
                message: t(R.strings.require_field, {
                  field: 'Tên đơn vị',
                }),
              },

              {
                pattern: CODE_REGEX,
                message: 'Mã đơn vị sai định dạng',
              },
            ]}
          >
            <BaseInput placeholder="Nhập mã đơn vị" />
          </BaseForm.Item>
        </BaseCol>
      </BaseRow>

      <BaseForm.Item name={'description'} label={t(R.strings.description)}>
        <TextArea placeholder="Nhập mô tả" />
      </BaseForm.Item>

      <BaseForm.Item
        rules={[
          ...getNormalRule({
            field: 'Công đoàn bộ phận',
          }),
        ]}
        label={'Công đoàn bộ phận'}
        name={'schoolId'}
      >
        <BaseSelect
          options={schoolData?.data?.map(item => ({
            label: item?.name,
            value: item?.id,
          }))}
          filterSort={() => 0}
          showSearch
          placeholder={'Chọn Công đoàn bộ phận'}
          loading={loadingSchool}
        />
      </BaseForm.Item>

      <BaseForm.Item
        name={'tradeUnionMemberIds'}
        label={'Thành viên công đoàn'}
        required
        rules={[
          {
            required: true,
            message: t(R.strings.require_field, {
              field: 'Thành viên công đoàn',
            }),
          },
        ]}
      >
        <BaseSelect
          placeholder={'Chọn thành viên công đoàn'}
          showSearch
          loading={isLoadingTradeUnionMember}
          mode="multiple"
          maxTagCount={2}
          options={tums?.map(item => ({
            label: `${item?.fullName} [${item?.mql}]`,
            value: item?.id,
          }))}
          onChange={ids => onChangeTumIds?.(ids as number[])}
        />
      </BaseForm.Item>

      <BaseForm.Item
        name={'type'}
        label={'Loại đơn vị'}
        required
        rules={[
          {
            required: true,
            message: t(R.strings.require_field, {
              field: 'Loại đơn vị',
            }),
          },
        ]}
      >
        <BaseSelect
          placeholder={'Chọn loại đơn vị'}
          showSearch
          options={[
            {
              value: UnitType.DEPARTMENT,
              label: 'Tổ công đoàn',
            },
            {
              value: UnitType.UNION_GROUP,
              label: 'Công đoàn bộ phận',
            },
          ]}
          onChange={value => onChangeUnitType(value as UnitType)}
        />
      </BaseForm.Item>

      {(() => {
        if (!unitType) return null
        switch (unitType) {
          case UnitType.DEPARTMENT:
            return (
              <BaseForm.Item
                name={'unionLeaderId'}
                label={'Tổ trưởng công đoàn'}
                required
                rules={[
                  {
                    required: true,
                    message: t(R.strings.require_field, {
                      field: 'Tổ trưởng công đoàn',
                    }),
                  },
                ]}
              >
                <BaseSelect
                  placeholder={'Chọn tổ trưởng công đoàn'}
                  showSearch
                  loading={isLoadingTradeUnionMember}
                  options={tumsCanSelect?.map(item => ({
                    label: `${item?.fullName} [${item?.mql}]`,
                    value: item?.id,
                  }))}
                  allowClear
                />
              </BaseForm.Item>
            )
          case UnitType.UNION_GROUP:
            return (
              <>
                <BaseForm.Item
                  name={'unionPresidentId'}
                  label={'Chủ tịch'}
                  required
                  rules={[
                    {
                      required: true,
                      message: t(R.strings.require_field, {
                        field: 'Chủ tịch',
                      }),
                    },
                  ]}
                >
                  <BaseSelect
                    placeholder={'Chọn chủ tịch'}
                    showSearch
                    loading={isLoadingTradeUnionMember}
                    options={tumsCanSelect?.map(item => ({
                      label: `${item?.fullName} [${item?.mql}]`,
                      value: item?.id,
                    }))}
                    allowClear
                  />
                </BaseForm.Item>

                <BaseForm.Item
                  name={'unionVicePresidentIds'}
                  label={'Phó chủ tịch'}
                >
                  <BaseSelect
                    placeholder={'Chọn phó chủ tịch'}
                    showSearch
                    loading={isLoadingTradeUnionMember}
                    options={tumsCanSelect?.map(item => ({
                      label: `${item?.fullName} [${item?.mql}]`,
                      value: item?.id,
                    }))}
                    mode="multiple"
                    allowClear
                  />
                </BaseForm.Item>

                <BaseForm.Item
                  name={'unionMemberIds'}
                  label={'Ủy viên Ban chấp hành'}
                  required
                  rules={[
                    {
                      required: true,
                      message: t(R.strings.require_field, {
                        field: 'Ủy viên Ban chấp hành',
                      }),
                    },
                  ]}
                >
                  <BaseSelect
                    placeholder={'Chọn ủy viên ban chấp hành'}
                    showSearch
                    loading={isLoadingTradeUnionMember}
                    options={tumsCanSelect?.map(item => ({
                      label: `${item?.fullName} [${item?.mql}]`,
                      value: item?.id,
                    }))}
                    mode="multiple"
                    allowClear
                  />
                </BaseForm.Item>
              </>
            )
        }
      })()}
    </BaseSpace>
  )
}
